/**
 * Rainfall / runoff constants, plus water-level classification.
 * Watch / alert cm values come from config (`waterlevel-alerts`) at runtime.
 */

/** Cumulative rainfall (mm) — IMD-style bands. */
export const RAINFALL_NONE_MAX_MM = 2.5;

export const RAINFALL_BANDS = [
  { category: "light", label: "Light", min: 2.5, max: 15.5 },
  { category: "moderate", label: "Moderate", min: 15.6, max: 64.4 },
  { category: "heavy", label: "Heavy", min: 64.5, max: 115.5 },
  { category: "very_heavy", label: "Very heavy", min: 115.6, max: 204.4 },
  {
    category: "extremely_heavy",
    label: "Extremely heavy",
    min: 204.5,
    max: null,
  },
];

/** Fallback only when config table has no valid below/above triggers. */
export const DEFAULT_WL_WATCH_CM = 20;
export const DEFAULT_WL_ALERT_CM = 30;

export const WL_WATCH_CM = DEFAULT_WL_WATCH_CM;
export const WL_ALERT_CM = DEFAULT_WL_ALERT_CM;
export const WL_WARNING_CM = DEFAULT_WL_ALERT_CM;
export const WL_DANGER_CM = DEFAULT_WL_ALERT_CM;
export const WL_HIGH_CM = DEFAULT_WL_WATCH_CM;
export const WL_CRITICAL_CM = DEFAULT_WL_ALERT_CM;

export const DEFAULT_WL_THRESHOLDS = {
  watchCm: DEFAULT_WL_WATCH_CM,
  alertCm: DEFAULT_WL_ALERT_CM,
  belowCm: DEFAULT_WL_WATCH_CM,
  aboveCm: DEFAULT_WL_ALERT_CM,
};

let runtimeWlThresholds = { ...DEFAULT_WL_THRESHOLDS };

export function resolveWlThresholds(input) {
  const below = Number(input?.belowCm ?? input?.watchCm);
  const above = Number(input?.aboveCm ?? input?.alertCm);
  const watchCm = Number.isFinite(below) ? below : DEFAULT_WL_WATCH_CM;
  const alertCm = Number.isFinite(above) ? above : DEFAULT_WL_ALERT_CM;
  if (!(watchCm < alertCm)) {
    return { ...DEFAULT_WL_THRESHOLDS };
  }
  return { watchCm, alertCm, belowCm: watchCm, aboveCm: alertCm };
}

export function setRuntimeWlThresholds(input) {
  runtimeWlThresholds = resolveWlThresholds(input);
  return runtimeWlThresholds;
}

export function getRuntimeWlThresholds() {
  return runtimeWlThresholds;
}

export function getWlScaleMaxCm(thresholds = runtimeWlThresholds) {
  const { alertCm } = resolveWlThresholds(thresholds);
  return Math.max(Math.ceil(alertCm * 1.25), alertCm + 10);
}

export const WL_SCALE_MAX_CM = getWlScaleMaxCm(DEFAULT_WL_THRESHOLDS);

export function getWaterLevelBands(thresholds = runtimeWlThresholds) {
  const { watchCm, alertCm } = resolveWlThresholds(thresholds);
  return [
    { category: "normal", label: "Normal", minCm: 0, maxCm: watchCm },
    { category: "watch", label: "Watch", minCm: watchCm, maxCm: alertCm },
    { category: "alert", label: "Alert", minCm: alertCm, maxCm: null },
  ];
}

export const WATER_LEVEL_BANDS = getWaterLevelBands(DEFAULT_WL_THRESHOLDS);

/** Rain intensity / runoff rate (mm/min). */
export const RUNOFF_HEAVY_MM_PER_MIN = 5;
export const RAIN_ACTIVE_INTENSITY_MM_PER_MIN = 0;

/** Rise/fall rate stability & alert thresholds. */
export const STABLE_WL_RATE_CM_PER_MIN = 0.5 / 60;
export const RAPID_WL_RISE_CM_PER_MIN = 2.54 / 60;
export const STABLE_RAINFALL_RATE_MM_PER_MIN = 0.1 / 60;
export const STABLE_RAIN_INTENSITY_MM_PER_MIN_PER_MIN = 0.05 / 60;

export function classifyRainfall(mm) {
  const value = Number(mm);
  if (mm == null || Number.isNaN(value) || value < RAINFALL_NONE_MAX_MM) {
    return "none";
  }
  for (const band of RAINFALL_BANDS) {
    if (
      band.max == null
        ? value >= band.min
        : value >= band.min && value <= band.max
    ) {
      return band.category;
    }
  }
  return "extremely_heavy";
}

export function classifyWaterLevelCm(cm, thresholds = runtimeWlThresholds) {
  const value = Number(cm);
  if (cm == null || Number.isNaN(value) || value < 0) return null;
  const bands = getWaterLevelBands(thresholds);
  for (const band of bands) {
    if (
      band.maxCm == null
        ? value >= band.minCm
        : value >= band.minCm && value < band.maxCm
    ) {
      return band.category;
    }
  }
  return "alert";
}

export function classifyRunoffRate(mmPerMin) {
  const value = Number(mmPerMin);
  if (mmPerMin == null || Number.isNaN(value) || value <= 0) return "none";
  if (value >= RUNOFF_HEAVY_MM_PER_MIN) return "heavy";
  return "light";
}

export function isElevatedWaterLevel(cm, thresholds = runtimeWlThresholds) {
  const category = classifyWaterLevelCm(cm, thresholds);
  return category != null && category !== "normal";
}

export function isCriticalWaterLevel(cm, thresholds = runtimeWlThresholds) {
  return classifyWaterLevelCm(cm, thresholds) === "alert";
}
